import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
import { hoyEcuador } from '../../utils/fechas';
import printerService from '../../services/printerService';
import billingService from '../../services/billingService';
import PagoMixto, { lineasMixtoIniciales, resumirPagoMixto } from '../../comun/PagoMixto';
import AvisoSaldoFavor, { saldoDelCliente, saldoAlcanza, EtiquetaSaldo } from '../../comun/AvisoSaldoFavor';
import SelectorVendedor, { leerUltimoVendedor } from '../../comun/SelectorVendedor';

const LoncherasEscolares = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Escuelas y estadísticas
    const [schools, setSchools] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [stats, setStats] = useState({
        total_lunchboxes: 0,
        total_sales_usd: 0,
        schools_active_today: 0,
        price_per_lunchbox: 1.00
    });
    const [schoolsData, setSchoolsData] = useState([]);
    const [recentOrders, setRecentOrders] = useState([]);
    const [selectedDate, setSelectedDate] = useState(hoyEcuador());

    // Formulario de Despacho Rápido de Loncheras
    const [selectedSchoolId, setSelectedSchoolId] = useState('');
    const [dispatchDate, setDispatchDate] = useState(() => hoyEcuador());
    const [quantity, setQuantity] = useState(25);
    const [paymentMethod, setPaymentMethod] = useState('efectivo');
    const [transferReference, setTransferReference] = useState('');
    const [orderStatus, setOrderStatus] = useState('completed'); // 'completed' = Entregado, 'pending' = Por entregar
    const [paymentStatus, setPaymentStatus] = useState('paid'); // 'paid' = Pagado, 'pending' = Por cobrar / Crédito
    const [notes, setNotes] = useState('');

    // Selector de Cliente
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerSearch, setCustomerSearch] = useState('');
    const [showCustomerPicker, setShowCustomerPicker] = useState(false);
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [newCustomer, setNewCustomer] = useState({ email: '', password: 'Password123!', password_confirmation: 'Password123!', first_name: '', last_name: '', phone: '', address: '', city: '', cedula: '' });

    const searchCustomers = async (query) => {
        setCustomerSearch(query);
        if (query.length < 3) { setCustomers([]); return; }
        try {
            const res = await api.post('/api/customers/admin/search/', { query }, { baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE });
            setCustomers(res.data.data.customers || []);
        } catch (err) { console.error('Error buscando clientes:', err); }
    };

    const handleCreateCustomer = async (e) => {
        e.preventDefault();
        try {
            const res = await api.post('/api/customers/register/', { ...newCustomer, cedula: newCustomer.cedula || null }, { baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE });
            const c = res.data.data.customer;
            alert('Cliente creado exitosamente');
            setShowCustomerModal(false);
            setSelectedCustomer(c);
            setCustomerSearch(`${c.first_name} ${c.last_name}`);
            setCustomers([]);
            setNewCustomer({ email: '', password: 'Password123!', password_confirmation: 'Password123!', first_name: '', last_name: '', phone: '', address: '', city: '', cedula: '' });
        } catch (err) {
            const e = err.response?.data;
            let msg = 'Error al crear cliente';
            if (e?.errors) msg += ':\n' + Object.entries(e.errors).map(([k,v]) => `- ${k}: ${v}`).join('\n');
            else if (e?.message) msg += ': ' + e.message;
            else msg += ': ' + err.message;
            alert(msg);
        }
    };

    // Pago Mixto: varias formas de pago en la misma venta (ej: $30 efectivo + $10 transferencia + $20 fiado)
    const [mixLines, setMixLines] = useState(lineasMixtoIniciales());
    const [exchangeRate] = useState(parseFloat(localStorage.getItem('usdExchangeRate')) || 4000);

    // Trabajador que realiza la venta
    const [sellerId, setSellerId] = useState(leerUltimoVendedor());

    // Modal para Nueva Escuela
    const [showSchoolModal, setShowSchoolModal] = useState(false);
    const [newSchool, setNewSchool] = useState({
        name: '',
        contact_person: '',
        phone: '',
        address: '',
        default_delivery_time: '09:30',
        notes: ''
    });

    // Modal para Ver Ticket de Despacho
    const [previewOrder, setPreviewOrder] = useState(null);
    const [voucherConfig, setVoucherConfig] = useState({
        business_name: "SOPHIA'S COFFEE",
        ruc: '0401788617001',
        establishment_address: 'Tulcán, Carchi - Ecuador'
    });

    useEffect(() => {
        loadData();
        loadVoucherConfig();
    }, [selectedDate]);

    const loadVoucherConfig = async () => {
        try {
            const data = await billingService.getConfig();
            if (data) {
                setVoucherConfig(data);
            }
        } catch (e) {
            console.error('Error cargando voucher config:', e);
        }
    };

    const loadData = async () => {
        setLoading(true);
        try {
            // Cargar Escuelas
            const schoolsRes = await api.get('/api/orders/schools/', {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            const schoolsList = Array.isArray(schoolsRes.data) ? schoolsRes.data : (schoolsRes.data.results || []);
            setSchools(schoolsList);

            // Verificar si viene una escuela en los query params (ej: ?schoolId=xxx)
            const queryParams = new URLSearchParams(location.search);
            const paramSchoolId = queryParams.get('schoolId');

            if (paramSchoolId && schoolsList.some(s => s.id === paramSchoolId)) {
                setSelectedSchoolId(paramSchoolId);
            } else if (schoolsList.length > 0 && !selectedSchoolId) {
                setSelectedSchoolId(schoolsList[0].id);
            }

            // Cargar Estadísticas y desglose por Escuela
            const statsRes = await api.get('/api/orders/schools/lunchbox_stats/', {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE,
                params: { date: selectedDate }
            });
            if (statsRes.data) {
                setStats(statsRes.data.summary || {});
                setSchoolsData(statsRes.data.schools || []);
            }

            // Cargar Órdenes de Loncheras de la fecha
            const ordersRes = await api.get('/api/orders/orders/', {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE,
                params: {
                    business_type: 'lonchera',
                    date_from: `${selectedDate}T00:00:00`,
                    date_to: `${selectedDate}T23:59:59`
                }
            });
            const ordersList = Array.isArray(ordersRes.data) ? ordersRes.data : (ordersRes.data.results || []);
            setRecentOrders(ordersList);

        } catch (err) {
            console.error('Error cargando datos de loncheras:', err);
        } finally {
            setLoading(false);
        }
    };

    const totalToPay = useMemo(() => {
        return parseFloat(quantity || 0) * (stats.price_per_lunchbox || 1.00);
    }, [quantity, stats.price_per_lunchbox]);

    // Resumen del pago mixto (efectivo, transferencia, tarjeta y/o fiado)
    const mixSummary = useMemo(
        () => resumirPagoMixto(mixLines, totalToPay, exchangeRate),
        [mixLines, totalToPay, exchangeRate]
    );

    // El cliente es opcional: lo fiado queda a cargo de la escuela y del vendedor
    const showCustomerField = showCustomerPicker || !!selectedCustomer;

    // Validación del pago: el mixto debe cubrir el total y el saldo a favor
    // necesita un cliente con saldo suficiente. Devuelve el motivo del bloqueo ('' si todo bien).
    const paymentBlockReason = useMemo(() => {
        const saldo = saldoDelCliente(selectedCustomer);
        if (paymentMethod === 'saldo') {
            if (saldo === null) return 'Selecciona el cliente';
            return saldoAlcanza(selectedCustomer, totalToPay) ? '' : 'Saldo a favor insuficiente';
        }
        if (paymentMethod !== 'dividido') return '';
        if (!mixSummary.valid) return 'Completar Pago Mixto';
        if (mixSummary.hasBalance && saldo === null) return 'Selecciona el cliente';
        if (mixSummary.hasBalance && mixSummary.balance_amount > saldo + 0.009) return 'Saldo a favor insuficiente';
        return '';
    }, [paymentMethod, mixSummary, selectedCustomer, totalToPay]);
    const isPaymentValid = !paymentBlockReason;

    // Registrar Despacho / Venta de Loncheras
    const handleRegisterDispatch = async (e) => {
        e.preventDefault();
        if (!selectedSchoolId) {
            alert('Por favor selecciona una escuela.');
            return;
        }
        const qty = parseInt(quantity, 10);
        if (isNaN(qty) || qty <= 0) {
            alert('Por favor ingresa una cantidad válida de loncheras.');
            return;
        }

        const schoolObj = schools.find(s => s.id === selectedSchoolId);
        const schoolName = schoolObj ? schoolObj.name : 'Escuela Tulcán';
        const totalNum = parseFloat(totalToPay.toFixed(2));
        const totalAmount = totalToPay.toFixed(2);

        let cashAmount = 0;
        let transferAmount = 0;
        let cardAmount = 0;
        let creditAmount = 0;
        let balanceAmount = 0;
        let splitNote = '';

        if (paymentMethod === 'dividido') {
            if (!mixSummary.valid) {
                alert(`Los montos del pago mixto ($${mixSummary.entered.toFixed(2)} USD) no cubren el Total a Cobrar ($${totalAmount} USD). Por favor, verifica los montos ingresados.`);
                return;
            }
            if (paymentBlockReason) {
                alert(`No se puede cobrar la parte con saldo a favor: ${paymentBlockReason.toLowerCase()}.`);
                return;
            }
            cashAmount = mixSummary.cash_amount;
            transferAmount = mixSummary.transfer_amount;
            cardAmount = mixSummary.card_amount;
            creditAmount = mixSummary.credit_amount;
            balanceAmount = mixSummary.balance_amount;
            splitNote = mixSummary.note;
        } else if (paymentMethod === 'saldo') {
            if (paymentBlockReason) {
                alert(`No se puede cobrar con saldo a favor: ${paymentBlockReason.toLowerCase()}.`);
                return;
            }
        } else if (paymentMethod === 'fiado') {
            cashAmount = 0;
            transferAmount = 0;
        } else {
            cashAmount = paymentMethod === 'efectivo' ? totalNum : 0;
            transferAmount = paymentMethod === 'transferencia' ? totalNum : 0;
        }

        setSubmitting(true);
        try {
            // Buscar o usar el producto de Lonchera Escolar ($1.00)
            const menuRes = await api.get('/api/menu/products/', {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE,
                params: { search: 'Lonchera Escolar' }
            });
            const products = Array.isArray(menuRes.data) ? menuRes.data : (menuRes.data.results || []);
            const lunchboxProduct = products[0];

            if (!lunchboxProduct) {
                throw new Error('No se encontró el producto Lonchera Escolar ($1.00) en el menú.');
            }

            const payloadNotes = [
                notes,
                `Escuela: ${schoolName}`,
                paymentMethod === 'transferencia' && transferReference ? `Ref: ${transferReference}` : '',
                splitNote
            ].filter(Boolean).join(' | ');

            const payload = {
                business_type: 'lonchera',
                order_date: dispatchDate,
                school_id: selectedSchoolId,
                school_name: schoolName,
                lunchbox_count: qty,
                order_type: 'delivery',
                payment_method: paymentMethod,
                payment_reference: paymentMethod === 'transferencia' ? transferReference : '',
                cash_amount: cashAmount,
                transfer_amount: transferAmount,
                card_amount: cardAmount,
                credit_amount: creditAmount,
                balance_amount: balanceAmount,
                customer_id: selectedCustomer ? selectedCustomer.id : null,
                seller_id: sellerId || null,
                delivery_info: {
                    address: (schoolObj && schoolObj.address) || 'Tulcán',
                    contact_name: (schoolObj && schoolObj.contact_person) || schoolName,
                    contact_phone: (schoolObj && schoolObj.phone) || '0999999999',
                    city: 'Tulcán'
                },
                items: [
                    {
                        product_id: lunchboxProduct.id,
                        quantity: qty,
                        notes: `Despacho escolar a ${schoolName} (${qty} cajitas felices)`
                    }
                ],
                notes: payloadNotes
            };

            const res = await api.post('/api/orders/orders/', payload, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });

            alert(`Despacho registrado exitosamente: ${qty} loncheras para ${schoolName} ($${totalAmount} USD)` +
                (paymentMethod === 'saldo' || balanceAmount > 0
                    ? `\nDescontado del saldo a favor de ${selectedCustomer.first_name} ${selectedCustomer.last_name}: $${(paymentMethod === 'saldo' ? totalNum : balanceAmount).toFixed(2)}`
                    : '') +
                (creditAmount > 0 ? `\nFiado a ${selectedCustomer ? `${selectedCustomer.first_name} ${selectedCustomer.last_name}` : schoolName}: $${creditAmount.toFixed(2)}` : ''));

            setNotes('');
            setTransferReference('');
            setMixLines(lineasMixtoIniciales());
            setPaymentMethod('efectivo');
            setSelectedCustomer(null);
            setCustomerSearch('');
            setCustomers([]);
            setShowCustomerPicker(false);
            loadData();

            // Abrir preview de ticket
            if (res.data) {
                setPreviewOrder({
                    ...res.data,
                    school_name: schoolName,
                    lunchbox_count: qty,
                    total: totalAmount,
                    notes: payloadNotes,
                    payment_method: paymentMethod
                });
            }

        } catch (err) {
            console.error('Error registrando despacho de loncheras:', err);
            const errDetail = err.response?.data?.detail || 
                (typeof err.response?.data === 'object' ? JSON.stringify(err.response?.data) : err.response?.data) || 
                err.message;
            alert('Error al registrar despacho: ' + errDetail);
        } finally {
            setSubmitting(false);
        }
    };

    // Crear Nueva Escuela
    const handleCreateSchool = async (e) => {
        e.preventDefault();
        if (!newSchool.name.trim()) {
            alert('El nombre de la escuela es obligatorio.');
            return;
        }

        try {
            await api.post('/api/orders/schools/', newSchool, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            alert('Escuela agregada exitosamente.');
            setShowSchoolModal(false);
            setNewSchool({
                name: '',
                contact_person: '',
                phone: '',
                address: '',
                default_delivery_time: '09:30',
                notes: ''
            });
            loadData();
        } catch (err) {
            console.error('Error creando escuela:', err);
            alert('Error al crear escuela: ' + (err.response?.data?.name?.[0] || err.message));
        }
    };

    // Imprimir Ticket de Despacho
    const printDispatchTicket = async (order) => {
        const dateStr = new Date().toLocaleDateString('es-EC');
        const timeStr = new Date().toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' });
        const schoolName = order.school_name || (order.school?.name) || 'Escuela de Tulcán';
        const qty = order.lunchbox_count || order.items?.reduce((sum, i) => sum + i.quantity, 0) || 0;
        const total = parseFloat(order.total || qty).toFixed(2);

        let paymentLabel = order.payment_method?.toUpperCase() || 'EFECTIVO';
        let splitLines = '';
        const mixedMatch = order.notes && order.notes.match(/\[MIXTO\] PAGADAS=(\d+) FIADAS=(\d+)/);
        if (mixedMatch) {
            const unitPrice = stats.price_per_lunchbox || 1.00;
            paymentLabel = 'MIXTO (PAGO + FIADO)';
            splitLines = `\nDetalle Pago Mixto:\n - Pagadas: ${mixedMatch[1]} = $${(mixedMatch[1] * unitPrice).toFixed(2)}\n - Fiadas:  ${mixedMatch[2]} = $${(mixedMatch[2] * unitPrice).toFixed(2)}`;
        } else if (order.payment_method === 'dividido' || (order.notes && order.notes.includes('[SPLIT]'))) {
            paymentLabel = 'MIXTO';
            if (order.notes) {
                const labels = [
                    ['CASH_USD', 'Efec. USD', '$'], ['TRANSFER_USD', 'Transf. USD', '$'], ['CARD_USD', 'Tarjeta USD', '$'],
                    ['CASH_COP', 'Efec. COP', 'COP '], ['TRANSFER_COP', 'Transf. COP', 'COP '], ['CARD_COP', 'Tarjeta COP', 'COP '],
                    ['CREDIT_USD', 'Fiado', '$']
                ];
                const parts = [];
                labels.forEach(([key, label, symbol]) => {
                    const match = order.notes.match(new RegExp(`${key}=([\\d.]+)`));
                    if (match && parseFloat(match[1]) > 0) parts.push(` - ${label}: ${symbol}${match[1]}`);
                });

                if (parts.length > 0) {
                    splitLines = `\nDetalle Pago Mixto:\n${parts.join('\n')}`;
                }
            }
        }

        const ticket = 
`================================
${voucherConfig.business_name || "SOPHIA'S COFFEE"}
RUC: ${voucherConfig.ruc || '0401788617001'}
${voucherConfig.establishment_address || 'Tulcán - Ecuador'}
================================
GUIA DE ENTREGA - LONCHERAS
No. Orden: ${order.order_sequence ? `#${order.order_sequence}` : (order.order_number || 'N/A')}
Fecha: ${dateStr} ${timeStr}
--------------------------------
DESTINO ESCOLAR:
${schoolName}
--------------------------------
Cant. Descripcion        Total
--------------------------------
${qty}x  Cajitas Felices $${total}
     (4 elementos c/u a $1.00)
--------------------------------
TOTAL A PAGAR:        $${total}
Forma de Pago: ${paymentLabel}${splitLines}
Estado: ENTREGADO
================================
Firma de Recepcion Escolar:


________________________________
¡Gracias por confiar en Sophia's Coffee!
`;
        try {
            await printerService.printCustomTicket(ticket, 'receipt');
            alert('Guía de entrega enviada a la impresora.');
        } catch (err) {
            window.print();
        }
    };

    return (
        <div style={{ padding: '24px', backgroundColor: 'var(--background-color)', minHeight: '100vh' }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                
                {/* Header Principal */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                                width: '48px', height: '48px', borderRadius: '12px',
                                backgroundColor: 'var(--primary-color)', color: 'white',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px'
                            }}>
                                <i className="bi bi-backpack-fill"></i>
                            </div>
                            <div>
                                <h1 style={{ fontSize: '26px', fontWeight: 'bold', color: 'var(--primary-color)', margin: 0 }}>
                                    Loncheras Escolares
                                </h1>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: '2px 0 0 0' }}>
                                    Gestión de cajitas felices ($1.00 c/u) y entregas para escuelas de Tulcán
                                </p>
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            style={{
                                padding: '8px 14px', borderRadius: '8px', border: '1px solid var(--border-color)',
                                backgroundColor: 'white', fontWeight: '500', color: 'var(--text-primary)', outline: 'none'
                            }}
                        />
                        <button
                            onClick={() => navigate('/fast-food/escuelas')}
                            style={{
                                backgroundColor: '#FAF6F2', color: 'var(--primary-color)', border: '1.5px solid #E6D7CD',
                                padding: '10px 16px', borderRadius: '8px', fontWeight: '700', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px'
                            }}
                            title="Ver catálogo completo, editar o crear escuelas"
                        >
                            <i className="bi bi-buildings"></i> Administrar Escuelas
                        </button>
                        <button
                            onClick={() => setShowSchoolModal(true)}
                            style={{
                                backgroundColor: 'var(--primary-color)', color: 'white', border: 'none',
                                padding: '10px 18px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px',
                                boxShadow: '0 2px 4px rgba(74, 31, 12, 0.2)'
                            }}
                        >
                            <i className="bi bi-building-add"></i> + Nueva Escuela
                        </button>
                    </div>
                </div>

                {/* Métricas / KPIs del Día */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                    <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '14px', border: '1px solid var(--border-color)', boxShadow: '0 2px 4px rgba(0,0,0,0.03)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Loncheras Hoy</span>
                            <i className="bi bi-box-seam" style={{ fontSize: '20px', color: 'var(--primary-color)' }}></i>
                        </div>
                        <div style={{ fontSize: '32px', fontWeight: '800', color: 'var(--primary-color)', marginTop: '8px' }}>
                            {stats.total_lunchboxes || 0}
                        </div>
                        <span style={{ fontSize: '12px', color: '#16a34a', fontWeight: '500' }}>Cajitas entregadas a $1.00</span>
                    </div>

                    <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '14px', border: '1px solid var(--border-color)', boxShadow: '0 2px 4px rgba(0,0,0,0.03)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Recaudación Total</span>
                            <i className="bi bi-cash-stack" style={{ fontSize: '20px', color: '#16a34a' }}></i>
                        </div>
                        <div style={{ fontSize: '32px', fontWeight: '800', color: '#16a34a', marginTop: '8px' }}>
                            ${Number(stats.total_sales_usd || 0).toFixed(2)}
                        </div>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Ingreso por loncheras escolares</span>
                    </div>

                    <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '14px', border: '1px solid var(--border-color)', boxShadow: '0 2px 4px rgba(0,0,0,0.03)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Escuelas Atendidas</span>
                            <i className="bi bi-buildings-fill" style={{ fontSize: '20px', color: 'var(--accent-color)' }}></i>
                        </div>
                        <div style={{ fontSize: '32px', fontWeight: '800', color: 'var(--primary-color)', marginTop: '8px' }}>
                            {stats.schools_active_today || 0}
                        </div>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>De {schools.length} escuelas registradas</span>
                    </div>

                    <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '14px', border: '1px solid var(--border-color)', boxShadow: '0 2px 4px rgba(0,0,0,0.03)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Precio Fijo por Lonchera</span>
                            <i className="bi bi-tag-fill" style={{ fontSize: '20px', color: 'var(--primary-color)' }}></i>
                        </div>
                        <div style={{ fontSize: '32px', fontWeight: '800', color: 'var(--primary-color)', marginTop: '8px' }}>
                            $1.00 <span style={{ fontSize: '16px', fontWeight: 'normal' }}>USD</span>
                        </div>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>4 elementos por cajita feliz</span>
                    </div>
                </div>

                {/* Contenido en 2 Columnas: Formulario de Despacho & Desglose por Escuela */}
                <div className="two-column-responsive-grid">
                    
                    {/* Columna Izquierda: Formulario de Despacho Rápido */}
                    <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid var(--border-color)', padding: '24px', boxShadow: '0 4px 12px rgba(74, 31, 12, 0.04)' }}>
                        <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--primary-color)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <i className="bi bi-send-plus-fill"></i> Registrar Despacho / Venta
                        </h2>

                        <form onSubmit={handleRegisterDispatch}>
                            {/* Fecha del Despacho / Venta */}
                            <div style={{ marginBottom: '16px', background: '#f8fafc', padding: '10px 12px', borderRadius: '10px', border: '1px solid var(--border-color)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                    <label style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <i className="bi bi-calendar-event" style={{ color: 'var(--primary-color)' }}></i> Fecha del Despacho / Venta
                                    </label>
                                    {dispatchDate !== hoyEcuador() ? (
                                        <span style={{ fontSize: '10px', background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '10px', fontWeight: 'bold' }}>
                                            Fecha Retroactiva
                                        </span>
                                    ) : (
                                        <span style={{ fontSize: '10px', background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '10px', fontWeight: '600' }}>
                                            Hoy
                                        </span>
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                    <input
                                        type="date"
                                        value={dispatchDate}
                                        onChange={(e) => setDispatchDate(e.target.value)}
                                        style={{
                                            flex: 1,
                                            padding: '8px 10px',
                                            borderRadius: '6px',
                                            border: '1px solid #cbd5e1',
                                            fontSize: '13px',
                                            fontWeight: '600',
                                            color: '#1e293b',
                                            backgroundColor: '#ffffff'
                                        }}
                                    />
                                    {dispatchDate !== hoyEcuador() && (
                                        <button
                                            type="button"
                                            onClick={() => setDispatchDate(hoyEcuador())}
                                            style={{
                                                padding: '0 10px',
                                                borderRadius: '6px',
                                                border: '1px solid #cbd5e1',
                                                background: '#f1f5f9',
                                                fontSize: '11px',
                                                fontWeight: '600',
                                                color: '#475569',
                                                cursor: 'pointer'
                                            }}
                                            title="Volver a hoy"
                                        >
                                            Hoy
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Trabajador que vendió */}
                            <SelectorVendedor value={sellerId} onChange={setSellerId} />

                            {/* Cliente: opcional, se muestra solo si se pide o si la venta tiene fiado */}
                            {showCustomerField ? (
                                <div style={{ marginBottom: '16px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px' }}>
                                            <i className="bi bi-person-fill" style={{ marginRight: '4px', color: 'var(--primary-color)' }}></i> Cliente (Opcional)
                                        </label>
                                        {!selectedCustomer && (
                                            <button type="button" onClick={() => { setShowCustomerPicker(false); setCustomerSearch(''); setCustomers([]); }}
                                                style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '12px', cursor: 'pointer', marginBottom: '6px' }}>
                                                Ocultar
                                            </button>
                                        )}
                                    </div>
                                    {selectedCustomer ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px' }}>
                                            <i className="bi bi-check-circle-fill" style={{ color: '#16a34a', fontSize: '16px' }}></i>
                                            <span style={{ flex: 1, fontSize: '13px', fontWeight: '600', color: '#15803d' }}>
                                                {selectedCustomer.first_name} {selectedCustomer.last_name}
                                                {selectedCustomer.cedula && <span style={{ fontWeight: 'normal', color: '#64748b' }}> · CI: {selectedCustomer.cedula}</span>}
                                                <EtiquetaSaldo customer={selectedCustomer} style={{ marginLeft: '6px' }} />
                                            </span>
                                            <button type="button" onClick={() => { setSelectedCustomer(null); setCustomerSearch(''); setCustomers([]); }}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '16px' }}><i className="bi bi-x-lg"></i></button>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', gap: '6px' }}>
                                            <div style={{ position: 'relative', flex: 1 }}>
                                                <input
                                                    type="text"
                                                    placeholder="Buscar por nombre, cédula o teléfono..."
                                                    value={customerSearch}
                                                    onChange={(e) => searchCustomers(e.target.value)}
                                                    style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', fontSize: '13px', outline: 'none' }}
                                                />
                                                {customers.length > 0 && (
                                                    <div style={{ position: 'absolute', zIndex: 10, width: '100%', backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', marginTop: '4px', maxHeight: '180px', overflowY: 'auto' }}>
                                                        {customers.map(c => (
                                                            <div key={c.id} style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
                                                                onClick={() => { setSelectedCustomer(c); setCustomerSearch(`${c.first_name} ${c.last_name}`); setCustomers([]); }}
                                                                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                                                onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                                                                <div style={{ fontWeight: '600', fontSize: '13px', color: 'var(--primary-color)' }}>{c.first_name} {c.last_name}</div>
                                                                <div style={{ fontSize: '11px', color: '#6b7280' }}>{c.email} {c.cedula && `· CI: ${c.cedula}`}</div>
                                                                <EtiquetaSaldo customer={c} style={{ marginTop: '4px' }} />
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <button type="button" onClick={() => setShowCustomerModal(true)} title="Crear nuevo cliente"
                                                style={{ width: '38px', height: '38px', background: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '20px', fontWeight: '300', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                +
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setShowCustomerPicker(true)}
                                    style={{
                                        marginBottom: '16px', padding: '6px 12px', borderRadius: '8px',
                                        border: '1px dashed #cbd5e1', background: 'white', color: '#475569',
                                        fontSize: '12px', fontWeight: '600', cursor: 'pointer'
                                    }}
                                >
                                    <i className="bi bi-person-plus"></i> Agregar cliente (opcional)
                                </button>
                            )}

                            {/* Escuela de Destino */}
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px' }}>
                                    Escuela de Tulcán *
                                </label>
                                <select
                                    value={selectedSchoolId}
                                    onChange={(e) => setSelectedSchoolId(e.target.value)}
                                    required
                                    style={{
                                        width: '100%', padding: '10px 14px', borderRadius: '8px',
                                        border: '1px solid var(--border-color)', fontSize: '14px', fontWeight: '500',
                                        backgroundColor: 'white', color: 'var(--text-primary)', outline: 'none'
                                    }}
                                >
                                    {schools.map(school => (
                                        <option key={school.id} value={school.id}>
                                            {school.name} ({school.default_delivery_time || '09:30'})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Cantidad de Loncheras */}
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px' }}>
                                    Cantidad de Cajitas Felices ($1.00 c/u) *
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={quantity}
                                    onChange={(e) => setQuantity(e.target.value)}
                                    required
                                    style={{
                                        width: '100%', padding: '12px 14px', borderRadius: '8px',
                                        border: '2px solid var(--primary-color)', fontSize: '18px', fontWeight: 'bold',
                                        color: 'var(--primary-color)', textAlign: 'center', outline: 'none'
                                    }}
                                />

                                {/* Botones rápidos de cantidad */}
                                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                    {[10, 25, 50, 75, 100].map(btnQty => (
                                        <button
                                            key={btnQty}
                                            type="button"
                                            onClick={() => setQuantity(btnQty)}
                                            style={{
                                                flex: 1, padding: '6px 0', borderRadius: '6px',
                                                border: '1px solid var(--border-color)',
                                                backgroundColor: quantity === btnQty ? 'var(--primary-color)' : 'var(--sidebar-bg)',
                                                color: quantity === btnQty ? 'white' : 'var(--primary-color)',
                                                fontWeight: '600', fontSize: '12px', cursor: 'pointer'
                                            }}
                                        >
                                            +{btnQty}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Resumen de Total a Cobrar */}
                            <div style={{
                                backgroundColor: 'var(--sidebar-bg)', padding: '16px', borderRadius: '12px',
                                border: '1px dashed var(--border-color)', marginBottom: '16px', textAlign: 'center'
                            }}>
                                <span style={{ fontSize: '12px', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: '600' }}>
                                    Total a Cobrar
                                </span>
                                <div style={{ fontSize: '28px', fontWeight: '800', color: 'var(--primary-color)' }}>
                                    ${totalToPay.toFixed(2)} <span style={{ fontSize: '14px', fontWeight: 'normal' }}>USD</span>
                                </div>
                                <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                                    {quantity || 0} loncheras x ${(stats.price_per_lunchbox || 1.00).toFixed(2)} USD
                                </span>
                            </div>

                            {/* Método de Pago */}
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px' }}>
                                    Forma de Pago
                                </label>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '8px' }}>
                                    <button
                                        type="button"
                                        onClick={() => setPaymentMethod('efectivo')}
                                        style={{
                                            padding: '10px 4px', borderRadius: '8px', border: '1px solid',
                                            borderColor: paymentMethod === 'efectivo' ? 'var(--primary-color)' : 'var(--border-color)',
                                            backgroundColor: paymentMethod === 'efectivo' ? 'var(--primary-light)' : 'white',
                                            color: paymentMethod === 'efectivo' ? 'var(--primary-color)' : 'var(--text-secondary)',
                                            fontWeight: '600', fontSize: '12px', cursor: 'pointer',
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <i className="bi bi-cash" style={{ fontSize: '18px' }}></i> Efectivo
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPaymentMethod('transferencia')}
                                        style={{
                                            padding: '10px 4px', borderRadius: '8px', border: '1px solid',
                                            borderColor: paymentMethod === 'transferencia' ? 'var(--primary-color)' : 'var(--border-color)',
                                            backgroundColor: paymentMethod === 'transferencia' ? 'var(--primary-light)' : 'white',
                                            color: paymentMethod === 'transferencia' ? 'var(--primary-color)' : 'var(--text-secondary)',
                                            fontWeight: '600', fontSize: '12px', cursor: 'pointer',
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <i className="bi bi-bank" style={{ fontSize: '18px' }}></i> Transferencia
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPaymentMethod('dividido')}
                                        style={{
                                            padding: '10px 4px', borderRadius: '8px', border: '1px solid',
                                            borderColor: paymentMethod === 'dividido' ? 'var(--primary-color)' : 'var(--border-color)',
                                            backgroundColor: paymentMethod === 'dividido' ? 'var(--primary-light)' : 'white',
                                            color: paymentMethod === 'dividido' ? 'var(--primary-color)' : 'var(--text-secondary)',
                                            fontWeight: '600', fontSize: '12px', cursor: 'pointer',
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <i className="bi bi-pie-chart" style={{ fontSize: '18px' }}></i> Mixto
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPaymentMethod('fiado')}
                                        style={{
                                            padding: '10px 4px', borderRadius: '8px', border: '1px solid',
                                            borderColor: paymentMethod === 'fiado' ? '#dc2626' : 'var(--border-color)',
                                            backgroundColor: paymentMethod === 'fiado' ? '#fee2e2' : 'white',
                                            color: paymentMethod === 'fiado' ? '#dc2626' : 'var(--text-secondary)',
                                            fontWeight: '700', fontSize: '12px', cursor: 'pointer',
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <i className="bi bi-file-earmark-text" style={{ fontSize: '18px' }}></i> Fiado
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setPaymentMethod('saldo'); setShowCustomerPicker(true); }}
                                        title="Descontar del saldo a favor (abono) del cliente"
                                        style={{
                                            padding: '10px 4px', borderRadius: '8px', border: '1px solid',
                                            borderColor: paymentMethod === 'saldo' ? '#7c3aed' : 'var(--border-color)',
                                            backgroundColor: paymentMethod === 'saldo' ? '#ede9fe' : 'white',
                                            color: paymentMethod === 'saldo' ? '#6d28d9' : 'var(--text-secondary)',
                                            fontWeight: '700', fontSize: '12px', cursor: 'pointer',
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <i className="bi bi-wallet2" style={{ fontSize: '18px' }}></i> Saldo a favor
                                    </button>
                                </div>

                                {paymentMethod === 'saldo' && (
                                    <div style={{ marginTop: '10px' }}>
                                        <AvisoSaldoFavor customer={selectedCustomer} total={totalToPay} />
                                    </div>
                                )}

                                {paymentMethod === 'fiado' && (
                                    <div style={{ marginTop: '10px', padding: '10px 12px', backgroundColor: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca', color: '#991b1b', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <i className="bi bi-exclamation-circle-fill" style={{ fontSize: '16px' }}></i>
                                        <span>
                                            Esta entrega se registrará como <strong>Cuenta por Cobrar (Fiado)</strong> para la institución educativa seleccionada.
                                        </span>
                                    </div>
                                )}

                                {paymentMethod === 'transferencia' && (
                                    <div style={{ marginTop: '10px' }}>
                                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#374151' }}>
                                            Número de Referencia (Opcional):
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Ej: 12345678"
                                            value={transferReference}
                                            onChange={(e) => setTransferReference(e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '8px 12px',
                                                border: '1px solid var(--border-color)',
                                                borderRadius: '8px',
                                                fontSize: '13px',
                                                outline: 'none'
                                            }}
                                        />
                                    </div>
                                )}

                                {paymentMethod === 'dividido' && (
                                    <div style={{ marginTop: '12px' }}>
                                        <PagoMixto
                                            lineas={mixLines}
                                            onChange={setMixLines}
                                            total={totalToPay}
                                            exchangeRate={exchangeRate}
                                            hasDebtor
                                            saldoDisponible={saldoDelCliente(selectedCustomer)}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Notas / Observaciones */}
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px' }}>
                                    Notas / Detalle de Entrega (Opcional)
                                </label>
                                <input
                                    type="text"
                                    placeholder="Ej: Entregar en el bar escolar / 5to grado"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    style={{
                                        width: '100%', padding: '10px 14px', borderRadius: '8px',
                                        border: '1px solid var(--border-color)', fontSize: '13px', outline: 'none'
                                    }}
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={submitting || !isPaymentValid}
                                style={{
                                    width: '100%', padding: '14px',
                                    backgroundColor: !isPaymentValid ? '#ef4444' : 'var(--primary-color)',
                                    color: 'white', border: 'none', borderRadius: '10px', fontWeight: 'bold',
                                    fontSize: '15px', cursor: (submitting || !isPaymentValid) ? 'not-allowed' : 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                    boxShadow: '0 4px 12px rgba(74, 31, 12, 0.25)', transition: 'all 0.2s'
                                }}
                            >
                                {submitting ? (
                                    <span>Registrando despacho...</span>
                                ) : !isPaymentValid ? (
                                    <>
                                        <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: '18px' }}></i>
                                        {`${paymentBlockReason} ($${totalToPay.toFixed(2)})`}
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check2-circle" style={{ fontSize: '18px' }}></i>
                                        Registrar Despacho (${totalToPay.toFixed(2)})
                                    </>
                                )}
                            </button>
                        </form>
                    </div>

                    {/* Columna Derecha: Desglose por Escuela & Historial del Día */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        
                        {/* Tabla de Desglose por Escuela */}
                        <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid var(--border-color)', padding: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: 'var(--primary-color)', margin: 0 }}>
                                    <i className="bi bi-buildings"></i> Estado de Entregas por Escuela (Hoy)
                                </h3>
                                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                    {schoolsData.length} escuelas registradas
                                </span>
                            </div>

                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: 'var(--sidebar-bg)', borderBottom: '1px solid var(--border-color)' }}>
                                            <th style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--text-primary)' }}>Escuela</th>
                                            <th style={{ padding: '10px 14px', textAlign: 'center', color: 'var(--text-primary)' }}>Hora Recreo</th>
                                            <th style={{ padding: '10px 14px', textAlign: 'center', color: 'var(--text-primary)' }}>Cajitas Despachadas</th>
                                            <th style={{ padding: '10px 14px', textAlign: 'right', color: 'var(--text-primary)' }}>Total ($)</th>
                                            <th style={{ padding: '10px 14px', textAlign: 'center', color: 'var(--text-primary)' }}>Acción Rápida</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {schoolsData.map(school => (
                                            <tr key={school.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                                <td style={{ padding: '12px 14px', fontWeight: '600', color: 'var(--text-primary)' }}>
                                                    {school.name}
                                                    {school.contact_person && (
                                                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 'normal' }}>
                                                            {school.contact_person}
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={{ padding: '12px 14px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                                                    {school.default_delivery_time || '09:30'}
                                                </td>
                                                <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                                                    {school.lunchboxes_count > 0 ? (
                                                        <span style={{
                                                            padding: '4px 10px', borderRadius: '12px', fontWeight: 'bold',
                                                            backgroundColor: 'var(--primary-light)', color: 'var(--primary-color)'
                                                        }}>
                                                            {school.lunchboxes_count} cajitas
                                                        </span>
                                                    ) : (
                                                        <span style={{ color: '#9ca3af' }}>0</span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '12px 14px', textAlign: 'right', fontWeight: 'bold', color: school.total_amount > 0 ? '#16a34a' : 'var(--text-secondary)' }}>
                                                    ${Number(school.total_amount || 0).toFixed(2)}
                                                </td>
                                                <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setSelectedSchoolId(school.id);
                                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                                        }}
                                                        style={{
                                                            padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--border-color)',
                                                            backgroundColor: 'var(--sidebar-bg)', color: 'var(--primary-color)',
                                                            fontSize: '11px', fontWeight: '600', cursor: 'pointer'
                                                        }}
                                                    >
                                                        Despachar
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Historial de Despachos Recientes */}
                        <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid var(--border-color)', padding: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: 'var(--primary-color)', margin: 0 }}>
                                    <i className="bi bi-clock-history"></i> Despachos Registrados ({selectedDate})
                                </h3>
                                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                    {recentOrders.length} envíos
                                </span>
                            </div>

                            {recentOrders.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)' }}>
                                    <i className="bi bi-inbox" style={{ fontSize: '32px', display: 'block', marginBottom: '8px', color: '#cbd5e1' }}></i>
                                    No hay despachos registrados para esta fecha.
                                </div>
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                        <thead>
                                            <tr style={{ backgroundColor: 'var(--sidebar-bg)', borderBottom: '1px solid var(--border-color)' }}>
                                                <th style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--text-primary)' }}>N° Orden</th>
                                                <th style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--text-primary)' }}>Escuela</th>
                                                <th style={{ padding: '10px 14px', textAlign: 'center', color: 'var(--text-primary)' }}>Loncheras</th>
                                                <th style={{ padding: '10px 14px', textAlign: 'right', color: 'var(--text-primary)' }}>Total</th>
                                                <th style={{ padding: '10px 14px', textAlign: 'center', color: 'var(--text-primary)' }}>Pago</th>
                                                <th style={{ padding: '10px 14px', textAlign: 'center', color: 'var(--text-primary)' }}>Ticket</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recentOrders.map(order => (
                                                <tr key={order.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                                    <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontWeight: 'bold', color: 'var(--primary-color)' }}>
                                                        #{order.order_sequence || order.order_number?.slice(-6)}
                                                    </td>
                                                    <td style={{ padding: '10px 14px', fontWeight: '600', color: 'var(--text-primary)' }}>
                                                        {order.school_name || order.school?.name || 'Escuela Tulcán'}
                                                    </td>
                                                    <td style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 'bold' }}>
                                                        {order.lunchbox_count || order.items?.reduce((s, i) => s + i.quantity, 0) || 0}
                                                    </td>
                                                    <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 'bold', color: '#16a34a' }}>
                                                        ${parseFloat(order.total || 0).toFixed(2)}
                                                    </td>
                                                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                                                        <span style={{
                                                            padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '600',
                                                            backgroundColor: order.payment_method === 'efectivo' ? '#dcfce7' : (order.payment_method === 'dividido' ? '#fef3c7' : '#e0f2fe'),
                                                            color: order.payment_method === 'efectivo' ? '#15803d' : (order.payment_method === 'dividido' ? '#b45309' : '#0284c7'),
                                                            border: `1px solid ${order.payment_method === 'efectivo' ? '#86efac' : (order.payment_method === 'dividido' ? '#fde68a' : '#7dd3fc')}`
                                                        }}>
                                                            {order.payment_method === 'dividido' ? 'MIXTO' : (order.payment_method?.toUpperCase() || 'EFECTIVO')}
                                                            {order.notes && order.notes.includes('[MIXTO]') ? ' · MIXTO' : ''}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                                                        <button
                                                            type="button"
                                                            onClick={() => printDispatchTicket(order)}
                                                            style={{
                                                                padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--border-color)',
                                                                backgroundColor: 'white', color: 'var(--primary-color)', cursor: 'pointer', fontSize: '12px'
                                                            }}
                                                            title="Imprimir Guía de Entrega"
                                                        >
                                                            <i className="bi bi-printer-fill"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>

                </div>

            </div>

            {/* Modal para Crear Nueva Escuela */}
            {showSchoolModal && (
                <div
                    onClick={() => setShowSchoolModal(false)}
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', zIndex: 2000, padding: '20px'
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: 'white', borderRadius: '16px', maxWidth: '500px', width: '100%',
                            padding: '24px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: 'var(--primary-color)' }}>
                                <i className="bi bi-building-add"></i> Agregar Escuela de Tulcán
                            </h3>
                            <button
                                onClick={() => setShowSchoolModal(false)}
                                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}
                            >
                                &times;
                            </button>
                        </div>

                        <form onSubmit={handleCreateSchool}>
                            <div style={{ marginBottom: '14px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                                    Nombre de la Escuela / Institución *
                                </label>
                                <input
                                    type="text"
                                    required
                                    placeholder="Ej: Escuela Cristóbal Colón"
                                    value={newSchool.name}
                                    onChange={(e) => setNewSchool({ ...newSchool, name: e.target.value })}
                                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                                        Contacto / Docente
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Ej: Lcda. Directora"
                                        value={newSchool.contact_person}
                                        onChange={(e) => setNewSchool({ ...newSchool, contact_person: e.target.value })}
                                        style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                                        Hora de Recreo / Entrega
                                    </label>
                                    <input
                                        type="time"
                                        value={newSchool.default_delivery_time}
                                        onChange={(e) => setNewSchool({ ...newSchool, default_delivery_time: e.target.value })}
                                        style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                                    />
                                </div>
                            </div>

                            <div style={{ marginBottom: '14px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                                    Dirección en Tulcán
                                </label>
                                <input
                                    type="text"
                                    placeholder="Ej: Calle Bolívar y Pichincha"
                                    value={newSchool.address}
                                    onChange={(e) => setNewSchool({ ...newSchool, address: e.target.value })}
                                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                                />
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowSchoolModal(false)}
                                    style={{
                                        padding: '10px 18px', borderRadius: '8px', border: '1px solid var(--border-color)',
                                        backgroundColor: 'white', color: 'var(--text-secondary)', fontWeight: '600', cursor: 'pointer'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    style={{
                                        padding: '10px 20px', borderRadius: '8px', border: 'none',
                                        backgroundColor: 'var(--primary-color)', color: 'white', fontWeight: '600', cursor: 'pointer'
                                    }}
                                >
                                    Guardar Escuela
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal de Ticket de Despacho */}
            {previewOrder && (
                <div
                    onClick={() => setPreviewOrder(null)}
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', zIndex: 2500, padding: '20px'
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: 'white', borderRadius: '16px', maxWidth: '380px', width: '100%',
                            padding: '20px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold', color: 'var(--primary-color)' }}>
                                <i className="bi bi-receipt"></i> Guía de Entrega Escolar
                            </h3>
                            <button
                                onClick={() => setPreviewOrder(null)}
                                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}
                            >
                                &times;
                            </button>
                        </div>

                        {/* Ticket Renderizado */}
                        <div style={{
                            backgroundColor: '#fff', padding: '16px', border: '1px solid #e2e8f0', borderRadius: '6px',
                            fontFamily: '"Courier New", Courier, monospace', fontSize: '11px', color: '#000', lineHeight: 1.3
                        }}>
                            <div style={{ textAlign: 'center', borderBottom: '1px dashed #000', paddingBottom: '8px', marginBottom: '8px' }}>
                                <div style={{ fontSize: '14px', fontWeight: 'bold' }}>{voucherConfig.business_name || "SOPHIA'S COFFEE"}</div>
                                <div>RUC: {voucherConfig.ruc || '0401788617001'}</div>
                                <div>{voucherConfig.establishment_address || 'Tulcán - Ecuador'}</div>
                            </div>

                            <div style={{ borderBottom: '1px dashed #000', paddingBottom: '6px', marginBottom: '6px' }}>
                                <div style={{ fontWeight: 'bold', textAlign: 'center' }}>GUIA DE ENTREGA - LONCHERAS</div>
                                <div>No: {previewOrder.order_sequence || previewOrder.order_number?.slice(-6) || '001'}</div>
                                <div>Fecha: {new Date().toLocaleDateString('es-EC')} {new Date().toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })}</div>
                                <div style={{ marginTop: '4px' }}><strong>Escuela:</strong> {previewOrder.school_name}</div>
                            </div>

                            <div style={{ borderBottom: '1px dashed #000', paddingBottom: '6px', marginBottom: '6px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                                    <span>Cant. Descripción</span>
                                    <span>Total</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', margin: '4px 0' }}>
                                    <span>{previewOrder.lunchbox_count}x Cajitas Felices ($1)</span>
                                    <span>${parseFloat(previewOrder.total || 0).toFixed(2)}</span>
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '13px', margin: '8px 0' }}>
                                <span>TOTAL A PAGAR:</span>
                                <span>${parseFloat(previewOrder.total || 0).toFixed(2)}</span>
                            </div>

                            <div style={{ borderTop: '1px dashed #000', paddingTop: '6px', marginBottom: '6px', fontSize: '11px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Forma de Pago:</span>
                                    <span style={{ fontWeight: 'bold' }}>
                                        {previewOrder.payment_method === 'dividido' ? 'MIXTO' : previewOrder.payment_method === 'mixto_fiado' ? 'MIXTO (PAGO + FIADO)' : (previewOrder.payment_method?.toUpperCase() || 'EFECTIVO')}
                                    </span>
                                </div>
                                {previewOrder.notes && previewOrder.notes.includes('[SPLIT]') && (
                                    <div style={{ marginTop: '4px', fontSize: '10px', color: '#333' }}>
                                        {previewOrder.notes.split('|').filter(n => n.includes('[SPLIT]') || n.includes('CASH_')).map((n, i) => (
                                            <div key={i}>{n.trim()}</div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div style={{ textAlign: 'center', fontSize: '9px', marginTop: '12px', borderTop: '1px dotted #000', paddingTop: '8px' }}>
                                Firma Recepción Escolar: _________________
                            </div>
                        </div>

                        <div style={{ marginTop: '16px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button
                                type="button"
                                onClick={() => printDispatchTicket(previewOrder)}
                                style={{
                                    padding: '8px 16px', backgroundColor: 'var(--primary-color)', color: 'white',
                                    border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '13px', cursor: 'pointer',
                                    display: 'inline-flex', alignItems: 'center', gap: '6px'
                                }}
                            >
                                <i className="bi bi-printer-fill"></i> Imprimir Guía POS
                            </button>
                        </div>
                    </div>
                </div>
            )}

        {/* Modal de Nuevo Cliente */}
        {showCustomerModal && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '1rem' }}>
                <div style={{ backgroundColor: '#fff', borderRadius: '14px', width: '100%', maxWidth: '460px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                    <div style={{ padding: '1.25rem 1.5rem', borderBottom: '2px solid #e5e7eb', background: 'var(--sidebar-bg)', borderRadius: '14px 14px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700', color: 'var(--primary-color)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <i className="bi bi-person-plus-fill"></i> Nuevo Cliente
                        </h3>
                        <button onClick={() => setShowCustomerModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#64748b' }}>×</button>
                    </div>
                    <form onSubmit={handleCreateCustomer} style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Cédula / RUC</label>
                            <input type="text" value={newCustomer.cedula} onChange={e => setNewCustomer(p => ({...p, cedula: e.target.value}))} placeholder="Ej: 0401234567"
                                style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1.5px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box' }} />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Correo Electrónico *</label>
                            <input type="email" required value={newCustomer.email} onChange={e => setNewCustomer(p => ({...p, email: e.target.value}))} placeholder="correo@ejemplo.com"
                                style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1.5px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box' }} />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Nombre *</label>
                                <input type="text" required value={newCustomer.first_name} onChange={e => setNewCustomer(p => ({...p, first_name: e.target.value}))} placeholder="Nombre"
                                    style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1.5px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box' }} />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Apellido *</label>
                                <input type="text" required value={newCustomer.last_name} onChange={e => setNewCustomer(p => ({...p, last_name: e.target.value}))} placeholder="Apellido"
                                    style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1.5px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box' }} />
                            </div>
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Teléfono</label>
                            <input type="text" value={newCustomer.phone} onChange={e => setNewCustomer(p => ({...p, phone: e.target.value}))} placeholder="0987654321"
                                style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1.5px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box' }} />
                        </div>
                        <div style={{ display: 'flex', gap: '0.75rem', paddingTop: '0.5rem', borderTop: '1px solid #e5e7eb' }}>
                            <button type="button" onClick={() => setShowCustomerModal(false)}
                                style={{ flex: 1, padding: '0.75rem', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>
                                Cancelar
                            </button>
                            <button type="submit"
                                style={{ flex: 2, padding: '0.75rem', background: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(74,31,12,0.2)' }}>
                                <i className="bi bi-person-check-fill"></i> Crear Cliente
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        )}

    </div>
    );
};

export default LoncherasEscolares;
